/** ImageNode metadata extends 28px below the creative; reserve it plus breathing room. */
export const CANVAS_AD_METADATA_HEIGHT = 28;
export const CANVAS_AD_ROW_GAP = 80;
export const CANVAS_AD_COLUMN_GAP = 40;
export const CANVAS_EDIT_COLUMN_GAP = 24;

export function canvasCellSize(items: Array<{ w: number; h: number }>) {
  return {
    width: Math.max(300, ...items.map(item => Number.isFinite(item.w) ? item.w : 0)),
    height: Math.max(300, ...items.map(item => Number.isFinite(item.h) ? item.h : 0)),
  };
}

/** Infer the existing grid before applying a changed cell size. */
export function canvasBatchOrigin(items: Array<{ x: number; y: number; slot: number }>, stepX: number, stepY: number) {
  const median = (values: number[]) => values.sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const oldStep = (axis: 'x' | 'y', fallback: number) => {
    const candidates: number[] = [];
    const index = (slot: number) => axis === 'x' ? slot % 5 : Math.floor(slot / 5);
    for (let a = 0; a < items.length; a++) for (let b = a + 1; b < items.length; b++) {
      const delta = index(items[b].slot) - index(items[a].slot);
      const stride = delta ? (items[b][axis] - items[a][axis]) / delta : 0;
      if (Number.isFinite(stride) && stride > 0) candidates.push(stride);
    }
    // A single misplaced tile must not redefine the grid. Infer only when
    // at least two independent pairs agree; otherwise retain the last stride.
    const counts = new Map<number, number>();
    for (const value of candidates) {
      const rounded = Math.round(value * 1000) / 1000;
      counts.set(rounded, (counts.get(rounded) ?? 0) + 1);
    }
    const ranked = Array.from(counts).sort((a, b) => b[1] - a[1] || Math.abs(a[0] - fallback) - Math.abs(b[0] - fallback));
    return ranked[0]?.[1] >= 2 ? ranked[0][0] : fallback;
  };
  const previousX = oldStep('x', stepX);
  const previousY = oldStep('y', stepY);
  return {
    x: median(items.map(item => item.x - (item.slot % 5) * previousX)),
    y: median(items.map(item => item.y - Math.floor(item.slot / 5) * previousY)),
  };
}

/** Keep subsequent workflow frames on the existing canvas left edge. */
export function canvasRunLeft(items: Array<{ x: number }>): number {
  const xs = items.map(item => item.x).filter(x => Number.isFinite(x) && x > CANVAS_OFFSCREEN_LIMIT);
  return xs.length ? Math.min(...xs) : 0;
}

/** Server reservation rows use this off-canvas range; it is never a visible run anchor. */
export const CANVAS_OFFSCREEN_LIMIT = -100_000;

/** Recover legacy sentinel-anchored batches in memory without rewriting saved account data. */
export function restoreCanvasPositions<T extends {id:string;x:number;y:number;displayWidth:number;workflowRunId?:number|null;canvasRecoveryLeft?:number}>(images:T[]):T[]{
  const visible=images.filter(image=>!(image.x<=CANVAS_OFFSCREEN_LIMIT&&image.displayWidth<=1&&/(?:^|-)spacer(?:-|$)/.test(image.id)));
  const invalid=visible.filter(image=>image.x<=CANVAS_OFFSCREEN_LIMIT);
  if(!invalid.length)return visible.length===images.length?images:visible;
  const anchors=visible.filter(image=>Number.isFinite(image.x)&&image.x>CANVAS_OFFSCREEN_LIMIT);
  const left=anchors.length?Math.min(...anchors.map(image=>image.canvasRecoveryLeft??image.x)):60;
  // Every affected producer anchored the batch to the same reservation X.
  // Keep that origin when tiles arrive one at a time or out of order.
  return visible.map(image=>{
    if(image.x>CANVAS_OFFSCREEN_LIMIT)return image;
    const recoveredSibling=anchors.find(anchor=>anchor.workflowRunId===image.workflowRunId&&anchor.canvasRecoveryLeft!=null);
    const runLeft=recoveredSibling?.canvasRecoveryLeft??left;
    return {...image,x:runLeft+image.x+999_999,canvasRecoveryLeft:runLeft};
  });
}
